import { FC, useState } from 'react'
import { useIntl } from 'react-intl'
import { Button, Stack } from '@instabox/consumer-web-ui'
import { AnimatePresence, motion } from 'framer-motion'
import { GetFaqsQuery, getTranslation } from '@instabox/consumer-web-shared'

import FAQLink from '../FAQLink'

export interface FAQListProps extends GetFaqsQuery {
  withTrim?: boolean
  animateInitial?: boolean
  maxItems?: number
}

export const DEFAULT_MAX_ITEMS = 3

export enum TrimState {
  Hidden,
  Disabled,
  Enabled,
}

const FAQList: FC<FAQListProps> = ({
  faqs = [],
  withTrim = true,
  maxItems = DEFAULT_MAX_ITEMS,
  animateInitial = false,
}) => {
  const { formatMessage } = useIntl()
  const [trimState, setTrimState] = useState<TrimState>(
    withTrim && faqs.length > maxItems ? TrimState.Enabled : TrimState.Hidden,
  )
  const faqItems = trimState === TrimState.Enabled ? faqs.slice(0, maxItems) : faqs

  const handleClick = () => {
    setTrimState(viewAllButton => {
      if (viewAllButton === TrimState.Enabled) return TrimState.Disabled
      if (viewAllButton === TrimState.Disabled) return TrimState.Enabled
      return viewAllButton
    })
  }

  const viewAllButtonVisible = trimState !== TrimState.Hidden
  const viewAllButtonContent =
    trimState === TrimState.Enabled
      ? formatMessage({ id: 'help.category.view_more' })
      : formatMessage({ id: 'help.category.view_less' })

  if (faqItems.length === 0) {
    return null
  }

  return (
    <AnimatedStack variants={containerVariants} initial="hidden" animate="visible" exit="hidden">
      <AnimatePresence initial={animateInitial}>
        {faqItems.map(({ slug, question, translations }) => (
          <AnimatedFAQLink variants={itemVariants} href={`/help/${slug}`} key={slug}>
            {getTranslation('question', translations, question)}
          </AnimatedFAQLink>
        ))}
      </AnimatePresence>
      {viewAllButtonVisible && (
        <Button variant="text" size="large" onClick={handleClick}>
          {viewAllButtonContent}
        </Button>
      )}
    </AnimatedStack>
  )
}

export default FAQList

const AnimatedStack = motion(Stack)
const AnimatedFAQLink = motion(FAQLink)

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      type: 'spring',
    },
  },
}

const itemVariants = {
  hidden: {
    height: 0,
    opacity: 0,
  },
  visible: {
    height: 'auto',
    opacity: 1,
  },
}
